/**
 * Scoring Rules for Planning Application Categorization
 * 
 * This module contains rules for adjusting category scores based on:
 * 1. Co-occurring words/phrases in the description (boosts and penalties)
 * 2. Application reference prefix shortcuts
 */

type CategoryScores = { [categoryId: string]: number };
type CategoryID = string;

/**
 * Precedence list of categories for fallback categorization
 * Used when no category scores above threshold
 */
export const PRECEDENCE: CategoryID[] = [
  'extensions_enlargements',
  'windows_doors_glazing',
  'interiors_accessibility',
  'roof_works_dormers',
  'outbuildings_ancillary',
  'change_of_use_conversions',
  'legal_admin',
  'new_build_major',
  'shopfront_signage',
  'parking_access',
  'landscaping_boundaries',
  'trees_ecology',
  'mechanical_services',
  'renewables_energy',
  'demolition',
  'external_lighting'
];

/**
 * Normalizes description text with light stemming 
 * to improve matching of variant forms of words
 */
export const normalizeWithStemming = (description: string): string => {
  // Guard against null, undefined, or non-string values
  if (description === null || description === undefined) return '';
  if (typeof description !== 'string') return String(description || '');
  
  return description
    .toLowerCase()
    .replace(/(\bextension)s?\b/g, 'extension')     // plurals
    .replace(/(\bextend)(ing|s|ed)\b/g, 'extend')
    .replace(/(\bconversion)s?\b/g, 'conversion')
    .replace(/(\bconvert)(ing|s|ed)?\b/g, 'convert')
    .replace(/(\bdwelling)s?\b/g, 'dwelling')
    .replace(/(\bstorey)(s)?\b/g, 'storey')
    .replace(/re[-\s]?roof(ing|ed)?/g, 're-roof')
    .replace(/(\bwindow)s?\b/g, 'window')
    .replace(/(\bdoor)s?\b/g, 'door')
    .replace(/(\bpartition)s?\b/g, 'partition')
    .replace(/(\bhouse)s?\b/g, 'house')
    .replace(/(\bdormers?\b)/g, 'dormer')
    .replace(/(\bflat)s?\b/g, 'flat');
};

/**
 * Applies boosts and penalties based on co-occurring words in the description
 */
export const applyBoostsAndPenalties = (
  description: string,
  scores: CategoryScores
): void => {
  const desc = description.toLowerCase();
  
  // New Build & Major Development penalties
  if (/\bextension\b|\bextend\b|\benlarge\b/i.test(desc) && scores['new_build_major'] > 0) {
    scores['new_build_major'] -= 6; // Penalize new_build if extension is detected
  }
  
  if (/\bdormer\b|\bloft\b|\bporch\b/i.test(desc) && scores['new_build_major'] > 0) {
    scores['new_build_major'] -= 6; // Penalize new_build for small additions
  }
  
  // New Build & Major Development boosts
  if (/\bunits\b|\bdwellings\b|\bplots\b|\bpurpose-built\b/i.test(desc)) {
    scores['new_build_major'] = (scores['new_build_major'] || 0) + 5; // Boost for large scale indicators
  }
  
  if (/\bstudent accommodation\b|\bflats\b|\bhotel\b/i.test(desc)) {
    scores['new_build_major'] = (scores['new_build_major'] || 0) + 5; // Boost for major developments
    
    // Penalty for extensions if these major terms are present
    if (scores['extensions_enlargements'] > 0) {
      scores['extensions_enlargements'] -= 4;
    }
  }
  
  // Interior works detection and boost
  if (/\binternal\b|\binterior\b|\bpartition\b|\breconfiguration\b|\bremodel\b|\bfit\s*out\b|\brefit(ted|ting)?\b|\brefurb(ishment|ish(ed)?)?\b/i.test(desc)) {
    scores['interiors_accessibility'] = (scores['interiors_accessibility'] || 0) + 4;
    
    // Penalties for mentioning interior with other categories
    if (scores['new_build_major'] > 0 && !/\bnew\b.*\b(building|development|structure)\b/i.test(desc)) {
      scores['new_build_major'] -= 5;
    }
  }
  
  // Shopfront & Signage specific boosts
  if (/\bfascia\b|\bprojecting sign\b|\badvertisement\b/i.test(desc)) {
    scores['shopfront_signage'] = (scores['shopfront_signage'] || 0) + 3;
  }
  
  // Change of Use specific boost
  if (/\bchange of use\b|\bchange the use\b|\bconvert(ing|ed)?\b|\bconversion\b/i.test(desc)) {
    scores['change_of_use_conversions'] = (scores['change_of_use_conversions'] || 0) + 6;
  }
  
  // Mechanical services boost (transformers/plant/ventilation etc.)
  if (/\btransformer\b|\bplant\b|\bplant room\b|\bventilation\b|\bflue\b|\bmechanical\b/i.test(desc)) {
    scores['mechanical_services'] = (scores['mechanical_services'] || 0) + 4;
  }
  
  // Outbuildings & small structures
  // Strong boost for explicit "garden store" (common outbuilding wording).
  if (/\bgarden\s+store\b/i.test(desc)) {
    scores['outbuildings_ancillary'] = (scores['outbuildings_ancillary'] || 0) + 6;
    // If a "garden store" is described as an "extension", treat it as an outbuilding rather than a house extension.
    if (scores['extensions_enlargements'] > 0) {
      scores['extensions_enlargements'] -= 4;
    }
  } else if (/\bcontainer\b.*\bunit\b|\bgarage\b|\bshed\b|\boutbuilding\b/i.test(desc)) {
    // Moderate boost for generic outbuilding terms.
    scores['outbuildings_ancillary'] = (scores['outbuildings_ancillary'] || 0) + 3;
  }
  
  // Driveways/landscaping should dominate when the proposal is primarily public realm / access works.
  // Avoid overpowering genuine major development cases where "parking/landscaping" is just a side-detail.
  const isMajorDevelopmentContext =
    (scores['new_build_major'] || 0) > 0 &&
    /\b(erect|build|construct|dwellings?|mews|homes?)\b/i.test(desc);

  if (!isMajorDevelopmentContext && /\bdriveway\b|\bparking\b|\brailings?\b|\bgates?\b|\blandscap/i.test(desc)) {
    scores['parking_access'] = (scores['parking_access'] || 0) + 4;
    scores['landscaping_boundaries'] = (scores['landscaping_boundaries'] || 0) + 4;
    
    if (scores['outbuildings_ancillary'] > 0) {
      scores['outbuildings_ancillary'] = Math.max(0, scores['outbuildings_ancillary'] - 4);
    }
  }
  
  // Disabled access ramps should be treated primarily as accessibility, not parking/access.
  if (/\bdisabled\b|\bwheelchair\b|\baccess ramp\b|\bdisabled access\b/i.test(desc)) {
    scores['interiors_accessibility'] = (scores['interiors_accessibility'] || 0) + 4;
  }
  
  // Roof works detection and boost
  if (/\broof\b|\bdormer\b|\bloft\b/i.test(desc)) {
    scores['roof_works_dormers'] = (scores['roof_works_dormers'] || 0) + 4;
  }
  
  // Small internal construction cases
  if (/\b(construct|build|erect|form)\s+(internal|partition|wall|door|window|bathroom|kitchen|study)\b/i.test(desc)) {
    // Significantly reduce new_build score for small construction
    scores['new_build_major'] = Math.max(0, (scores['new_build_major'] || 0) - 7);
    // Boost interiors score
    scores['interiors_accessibility'] = (scores['interiors_accessibility'] || 0) + 5;
  }
  
  // Special boost for garage conversion
  if (/\bgarage\s*conversion\b/i.test(desc)) {
    scores['change_of_use_conversions'] = (scores['change_of_use_conversions'] || 0) + 4;
    scores['outbuildings_ancillary'] = (scores['outbuildings_ancillary'] || 0) + 3;
  }
};

/**
 * Applies shortcuts based on application reference prefixes
 */
export const applyRefPrefixShortcuts = (
  referenceNo: string,
  scores: CategoryScores
): void => {
  if (!referenceNo) return;
  
  // Extract the suffix after the last slash
  const parts = referenceNo.split('/');
  const suffix = parts.length > 1 ? parts[parts.length - 1] : '';
  
  // Advertisement Consent
  if (suffix === 'ADV') {
    scores['shopfront_signage'] = (scores['shopfront_signage'] || 0) + 5;
  }
  
  // Major pre-application
  if (suffix === 'PAN') {
    scores['new_build_major'] = (scores['new_build_major'] || 0) + 5;
  }
  
  // Listed Building Consent or Windows
  if (suffix === 'LBC' || suffix === 'WINPA') {
    scores['windows_doors_glazing'] = (scores['windows_doors_glazing'] || 0) + 4;
  }
  
  // Lawful Development Certificates
  if (suffix === 'CLP' || suffix === 'CLE') {
    scores['legal_admin'] = (scores['legal_admin'] || 0) + 4;
  }
  
  // Full Planning Application
  if (suffix === 'FUL') {
    // No specific boost, but might be useful for future rules
  }
  
  // Listed Building Consent
  if (suffix === 'LBC') {
    // Also boost other historic-related categories
    scores['windows_doors_glazing'] = (scores['windows_doors_glazing'] || 0) + 4;
  }

  // Non-material variation / variation of condition (administrative)
  if (suffix === 'VARY' || suffix === 'NMA' || suffix === 'S42') {
    scores['legal_admin'] = (scores['legal_admin'] || 0) + 5;
  }
};

/**
 * Applies short description boost for cases with very few words
 */
export const applyShortDescriptionBoost = (
  description: string,
  scores: CategoryScores,
  boostAmount: number = 2
): void => {
  // Count actual words, not just whitespace
  const wordCount = description.split(/\s+/).filter(word => word.trim().length > 0).length;
  
  if (wordCount <= 6) {
    // For very short descriptions, boost all scores to get them over the threshold
    Object.keys(scores).forEach(key => {
      if (scores[key] > 0) {
        scores[key] += boostAmount;
      }
    });
  }
};

/**
 * Mini-pipeline for categorization as described in the spec
 */
export const categorizeWithRules = (
  description: string,
  referenceNo: string,
  initialScores: CategoryScores
): {
  primaryCategory: string | null;
  secondaryCategories: string[];
  lowConfidence: boolean;
} => {
  // Clone the initial scores to avoid mutating the original
  const scores = { ...initialScores };
  
  // Apply boosts and penalties
  applyBoostsAndPenalties(description, scores);
  
  // Apply reference prefix shortcuts
  applyRefPrefixShortcuts(referenceNo, scores);
  
  // Apply short description boost if needed
  applyShortDescriptionBoost(description, scores);
  
  // Sort categories by score (highest first)
  const sortedEntries = Object.entries(scores)
    .filter(([_, score]) => score > 0) // Only include categories with a score
    .sort((a, b) => b[1] - a[1]);
  
  // Extract the primary category using elastic threshold
  let primaryCategory: string | null = null;
  let lowConfidence = false;
  
  if (sortedEntries.length > 0) {
    const topScore = sortedEntries[0][1];
    
    if (topScore >= 6) {
      // Strong match
      primaryCategory = sortedEntries[0][0];
    } else if (topScore >= 3) {
      // Lower confidence match
      primaryCategory = sortedEntries[0][0];
      lowConfidence = true;
    } else {
      // Very low score - use precedence list
      for (const categoryId of PRECEDENCE) {
        if (scores[categoryId] > 0) {
          primaryCategory = categoryId;
          lowConfidence = true;
          break;
        }
      }
    }
  }
  
  // Create sorted array of just category IDs for secondaries
  const sortedCategories = sortedEntries.map(([category]) => category);
  
  // Extract secondary categories (excluding the primary)
  const secondaryCategories = sortedCategories
    .filter(category => category !== primaryCategory)
    .slice(0, 2); // Limit to top 2 secondary categories
  
  return {
    primaryCategory,
    secondaryCategories,
    lowConfidence
  };
};

/**
 * Quick keyword map for baseline scoring – used by `enhancedCategorize` to generate initial scores.
 * The intent is NOT exhaustive NLP – just enough to give reasonable hints before adjustment rules run.
 */
const KEYWORD_MAP: Record<CategoryID, RegExp[]> = {
  shopfront_signage: [/advert(isement)?/i, /fascia/i, /sign(age)?/i],
  extensions_enlargements: [/extension/i, /extend(ed|ing)?/i, /rear lounge extension/i],
  new_build_major: [/new\s+build/i, /\bunits\b/i, /dwellings?/i, /mews/i, /erect/i, /student accommodation/i],
  demolition: [/demolition/i, /demolish/i],
  landscaping_boundaries: [/landscap/i, /railings?/i, /gates?/i, /driveway/i, /surface/i, /wall/i],
  parking_access: [/driveway/i, /parking/i, /access/i, /ramp/i],
  change_of_use_conversions: [/change of use/i, /conversion/i, /convert/i],
  interiors_accessibility: [/interior/i, /internal/i, /partition/i, /accessibility/i, /disabled/i, /ramp/i],
  windows_doors_glazing: [/window/i, /door/i, /glazing/i, /louvre/i],
  mechanical_services: [/transformer/i, /plant/i, /ventilation/i, /flue/i, /mechanical/i],
  outbuildings_ancillary: [/garden\s+store/i, /shed/i, /outbuilding/i, /garage/i],
  legal_admin: [/non[- ]material variation/i, /variation/i, /VARY/i, /NMA/i, /section 42/i]
};

/**
 * Convenience wrapper expected by the Jest tests.
 * It analyses the description + AI summary, builds baseline scores, then calls the full categorization pipeline.
 */
export const enhancedCategorize = (
  description: string,
  aiSummary: string | undefined,
  referenceNo: string
) => {
  const combinedText = `${description || ''} ${aiSummary || ''}`;

  // Start with zeroed scores
  const initialScores: CategoryScores = {};

  // Apply keyword-based baselines
  Object.entries(KEYWORD_MAP).forEach(([categoryId, patterns]) => {
    if (patterns.some((re) => re.test(combinedText))) {
      initialScores[categoryId] = (initialScores[categoryId] || 0) + 4; // baseline +4
    }
  });

  // Feed into main pipeline
  return categorizeWithRules(combinedText, referenceNo, initialScores);
};
